import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Row,
  Card,
  CardBody,
  CardTitle,
  Button,
  Label,
  Input,
  Spinner,
  type InputProps
} from "reactstrap";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import IntlMessages from "Util/IntlMessages";
import { useAsyncResource } from "../../../../hooks/useAsyncResource";

type SettingsField = { name: string; labelId: string; type?: string };

type SimpleSettingsPageProps = {
  breadcrumbHeadingId: string;
  titleId: string;
  fetchSettings: () => Promise<Record<string, string>>;
  onSave: (values: Record<string, string>) => Promise<void | unknown>;
  fields: SettingsField[];
};

export default function SimpleSettingsPage({
  breadcrumbHeadingId,
  titleId,
  fetchSettings,
  onSave,
  fields
}: SimpleSettingsPageProps) {
  const { register, handleSubmit, reset } = useForm();
  const { data, loading, error } = useAsyncResource(fetchSettings, []);

  useEffect(() => {
    if (data) reset(data);
  }, [data, reset]);

  const [saving, setSaving] = React.useState(false);

  const submit = async (values: Record<string, string>) => {
    setSaving(true);
    try {
      await onSave(values);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="suite-settings">
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer heading={<IntlMessages id={breadcrumbHeadingId} />} />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Row>
        <Colxx md="8" lg="6">
          <Card>
            <CardBody>
              <CardTitle>
                <IntlMessages id={titleId} />
              </CardTitle>
              {loading && <Spinner />}
              {error && <p className="text-danger">{error}</p>}
              {!loading && (
                <form onSubmit={handleSubmit(submit)}>
                  {fields.map((f: SettingsField) => {
                    const { ref, ...rest } = register(f.name);
                    return (
                      <div key={f.name} className="mb-3">
                        <Label for={f.name}>
                          <IntlMessages id={f.labelId} />
                        </Label>
                        <Input
                          id={f.name}
                          type={(f.type || "text") as NonNullable<InputProps["type"]>}
                          innerRef={ref}
                          {...rest}
                        />
                      </div>
                    );
                  })}
                  <Button color="primary" type="submit" disabled={saving}>
                    {saving ? <Spinner size="sm" /> : <IntlMessages id="suite.action.save" />}
                  </Button>
                </form>
              )}
            </CardBody>
          </Card>
        </Colxx>
      </Row>
    </div>
  );
}
